/**
 * The buyer URL that renders this store's DRAFT.
 *
 * The buyer already knows how to do this — `page.tsx` reads an
 * `x-a2z-preview-token`, loads the draft theme instead of the live one, and its
 * `RESTAURANT` branch renders whatever theme it was handed. So a restaurant
 * editor needs no new rendering path: it needs a URL.
 *
 * ## Deriving the host
 *
 * The portal is served from `restaurant.<tail>` and a storefront from
 * `<slug>.<tail>`, so the buyer origin is this window's host with the first
 * label swapped. That is the same rule the seller's canvas uses, and it is
 * deliberately derived rather than configured: a `VITE_BUYER_ORIGIN` would be
 * a second source of truth for something the URL bar already states, and the
 * one that goes stale is always the env var.
 *
 * On a bare host with no subdomain (a plain `localhost` dev run) there is no
 * label to swap, so the buyer is addressed by PATH — `/{locale}/{slug}` — which
 * is the same shape the buyer serves there.
 */
export function buildPreviewUrl({
  storeSlug,
  locale,
  token,
  /** Bumped on every draft write to force the iframe to re-fetch. */
  version,
  host = typeof window === "undefined" ? "" : window.location.host,
  protocol = typeof window === "undefined" ? "https:" : window.location.protocol,
}: {
  storeSlug: string;
  locale: string;
  token: string;
  version?: number;
  host?: string;
  protocol?: string;
}): string {
  if (!host || !storeSlug || !token) return "";

  const labels = host.split(".");
  const base =
    labels.length > 1
      ? `${protocol}//${storeSlug}.${labels.slice(1).join(".")}/${locale}`
      : `/${locale}/${storeSlug}`;

  const query = new URLSearchParams({ preview: token });
  /*
   * A cache-buster, not decoration. The draft page is served through the same
   * ISR path as the live one; without a changing key the iframe would show the
   * seller their previous save and read as "my change did nothing".
   */
  if (version !== undefined) query.set("v", String(version));
  return `${base}?${query.toString()}`;
}

/**
 * The origin the preview iframe is served from.
 *
 * Needed twice over: as `targetOrigin` when messaging the bridge, and to reject
 * messages that did not come from it. A `postMessage` with `"*"` would hand the
 * seller's draft to any frame that happened to be listening.
 */
export function buyerOrigin({
  storeSlug,
  host = typeof window === "undefined" ? "" : window.location.host,
  protocol = typeof window === "undefined" ? "https:" : window.location.protocol,
}: {
  storeSlug: string;
  host?: string;
  protocol?: string;
}): string {
  if (!host) return "";
  const labels = host.split(".");
  // A bare host serves the buyer by path, so the origin is simply this one.
  if (labels.length <= 1) return `${protocol}//${host}`;
  return `${protocol}//${storeSlug}.${labels.slice(1).join(".")}`;
}

/**
 * The LIVE storefront URL — what a customer scanning the table QR actually
 * gets (UX-round D60, slice 10.10).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The owner's concern 16, in their own words: *"this is the page a customer
 * gets when they scan the QR on my table, and the screen will not tell me what
 * it looks like right now."* The settings screen badged «منشور» beside «مفيش
 * تصميمات متاحة دلوقتي» and offered Publish — three claims, none of which was
 * the one thing the page exists to answer.
 *
 * Same host-swap rule as `buildPreviewUrl` above and deliberately sharing it:
 * two functions deriving the buyer origin two ways is how a preview and a live
 * link end up pointing at different stores. The difference is the whole point —
 * **no preview token and no cache-buster**, because this is the published page,
 * not the draft.
 */
export function buildStorefrontUrl({
  storeSlug,
  locale,
  host = typeof window === "undefined" ? "" : window.location.host,
  protocol = typeof window === "undefined" ? "https:" : window.location.protocol,
}: {
  storeSlug: string;
  locale: string;
  host?: string;
  protocol?: string;
}): string {
  if (!host || !storeSlug) return "";
  const labels = host.split(".");
  return labels.length > 1
    ? `${protocol}//${storeSlug}.${labels.slice(1).join(".")}/${locale}`
    : `/${locale}/${storeSlug}`;
}

/**
 * The permanent public URL printed on a restaurant table.
 *
 * It deliberately reuses `buildStorefrontUrl`, so preview, live storefront,
 * and table stickers cannot drift onto different hosts. The QR contains only
 * the public Arabic route and the table code; the one-hour dining credential
 * is issued after the diner scans and passes the location check.
 */
export function buildTableQrUrl({
  storeSlug,
  tableCode,
  host = typeof window === "undefined" ? "" : window.location.host,
  protocol = typeof window === "undefined" ? "https:" : window.location.protocol,
}: {
  storeSlug: string;
  tableCode: string;
  host?: string;
  protocol?: string;
}): string {
  if (!isTableQrCodeGeneratable(tableCode)) return "";
  const storefront = buildStorefrontUrl({
    storeSlug,
    locale: "ar",
    host,
    protocol,
  });
  return storefront ? `${storefront}/t/${encodeURIComponent(tableCode)}` : "";
}

/** Dot segments are consumed by URL parsers and cannot identify a QR table. */
export function isTableQrCodeGeneratable(tableCode: string): boolean {
  return tableCode !== "" && tableCode !== "." && tableCode !== "..";
}

/**
 * The shape a code TYPED into the floor editor must take (B179) — the same
 * rule as the backend's `RESTAURANT_TABLE_CODE_PATTERN`. The production edge
 * firewall 403s dot-segments and paths ending in a file type no app serves
 * (`vip.log`, `a.json`), so a sticker printed from such a code would fail in
 * production only. Existing codes still print (`isTableQrCodeGeneratable`):
 * this guards what is typed next, not what is already on a table.
 */
export function isTypedTableCodeValid(tableCode: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_-]{0,39}$/.test(tableCode);
}
